// src/app/core/news/news.service.ts
import { Injectable } from '@angular/core';
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
} from 'firebase/firestore/lite';
import { getDownloadURL, ref } from 'firebase/storage';
import { dbLite, publicStorage } from '../../firebase/firebase-public-init';

export interface Article {
  slug: string; // id doc
  title: string;
  date: Date;
  excerpt?: string;
  img?: string; // path sau URL absolut (din câmpul .img)
  featuredUrl: string; // URL absolut pregătit pentru UI
  content?: string; // html
}

@Injectable({ providedIn: 'root' })
export class NewsService {
  /** Colecția de articole: /website/noutati/articole */
  private readonly basePath = ['website', 'noutati', 'articole'] as const;

  /** Lista articolelor din colecția de mai sus, ordonate desc după date */
  async list(): Promise<Article[]> {
    const q = query(
      collection(dbLite, ...this.basePath),
      orderBy('date', 'desc'),
    );
    const snap = await getDocs(q);

    const items = await Promise.all(
      snap.docs
        .filter((d) => (d.data() as any).published !== false)
        .map(async (d) => {
          const data: any = d.data();

          const featuredUrl = await this.resolveImageUrl(
            data.img ?? data.featuredUrl ?? data.featuredPath ?? '',
          );

          const date =
            data.date?.toDate?.() instanceof Date
              ? data.date.toDate()
              : typeof data.date === 'string'
                ? new Date(data.date)
                : new Date();

          return {
            slug: d.id,
            title: data.title ?? '(Fără titlu)',
            date,
            excerpt: data.excerpt ?? '',
            img: data.img,
            featuredUrl,
            content: data.content ?? '',
          } as Article;
        }),
    );

    return items;
  }

  /** Citește un articol după ID-ul documentului (/website/noutati/articole/{slug}) */
  async getBySlug(slug: string): Promise<Article | null> {
    if (!slug) return null;
    const refDoc = doc(dbLite, ...this.basePath, slug);
    const snap = await getDoc(refDoc);
    if (!snap.exists()) return null;

    const data: any = snap.data();
    if (data.published === false) return null;

    const featuredUrl = await this.resolveImageUrl(
      data.img ?? data.featuredUrl ?? data.featuredPath ?? '',
    );

    const date =
      data.date?.toDate?.() instanceof Date
        ? data.date.toDate()
        : typeof data.date === 'string'
          ? new Date(data.date)
          : new Date();

    return {
      slug: snap.id,
      title: data.title ?? '(Fără titlu)',
      date,
      excerpt: data.excerpt ?? '',
      img: data.img,
      featuredUrl,
      content: data.content ?? '',
    } as Article;
  }

  /** Acceptă URL absolut sau path în Storage și returnează URL download. */
  private async resolveImageUrl(value: string): Promise<string> {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value; // e deja URL
    return await getDownloadURL(ref(publicStorage, value)); // e path în Storage
  }
}
